import { AboveTheFoldOnlyServerRender } from 'above-the-fold-only-server-render';
import React, { PropTypes } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import Checkbox from '@bibliocommons/base-checkbox';
import { PrintButton } from '@bibliocommons/print';
import BcReactComponent from '@bibliocommons/base-bc-react-component';
import ViewSelector from '../ViewSelector';
import SortDropDown from '@bibliocommons/sort-dropdown';
import Pagination from '@bibliocommons/pagination';
import ActiveFilters from '../ActiveFilters';
import FeaturedContentContainer from '../../../containers/FeaturedContentContainer';
import RSSButton from '../RSSButton';
import FacetDrawerToggle from '../../shared/Facets/FacetDrawerToggle';
import SearchResultItem from '../SearchResultItem';

import DidNotFindExpandablePanel from '../DidNotFindExpandablePanel';

import './SearchResults.scss';

class SearchResults extends BcReactComponent {
  constructor(props) {
    super(props);
    this.bindAll('renderGroupingToggle');
  }

  componentDidMount() {
    this.fetchGroupSearchSummaries();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.results !== prevProps.results ||
      this.props.currentUser !== prevProps.currentUser
    ) {
      this.fetchGroupSearchSummaries();
    }
  }

  getPrintUrl() {
    const { search } = this.props.location;
    return `/v2/print/search${search}`;
  }

  renderResultItems() {
    const startIndex = ((this.props.pagination.get('page') - 1) * this.props.pagination.get('limit')) + 1;
    return this.props.results.valueSeq().map((result, index) => (
      <AboveTheFoldOnlyServerRender skip={index > 4} key={index}>
        <SearchResultItem
          dataResultRank={`${startIndex + index}`}
          dataKey="search-result-item"
          currentView={this.props.currentView}
          order={index > this.props.featuredContentBlockPosition ? 2 : 0}
          showSeries={this.props.searchType === 'series'}
          item={result.representative}
          key={result.representative.get('id')}
          availabilityDetail={this.props.availabilityDetail}
          availabilityDetailActions={this.props.availabilityDetailActions}
          catalogSearchOptions={this.props.catalogSearchOptions}
          manifestations={result.manifestations}
          showJacketCover={this.props.currentView !== 'small'}
          groupSearchFeedbackActions={this.props.groupSearchFeedbackActions}
          branches={this.props.branches}
          currentUser={this.props.currentUser} />
      </AboveTheFoldOnlyServerRender>
    ));
  }

  fetchGroupSearchSummaries() {
    if (this.shouldShowGroupingToggle()) {
      this.props.searchActions.getGroupSearchSummaries(
        this.props.results
          .map(result => result.manifestations)
          .flatten(true)
          .map(result => result.get('id'))
          .toArray()
      );
    }
  }

  shouldShowGroupingToggle() {
    return (
      this.props.currentUser &&
      this.props.currentUser.get('showGroupingDebug') &&
      this.props.currentView === 'grouped'
    );
  }

  renderGroupingToggle() {
    const { searchActions, catalogSearchOptions } = this.props;

    if (!this.shouldShowGroupingToggle()) {
      return null;
    }

    return (
      <div className="grouping-summary-debug hidden-xs">
        <Checkbox
          id="grouping-summary-debug"
          label="Debug Groupings"
          handleChange={searchActions.toggleShowGroupings}
          checked={catalogSearchOptions.get('showGroupings')}
          inline
          labelBefore />
      </div>
    );
  }

  renderActiveFilters() {
    const { activeFilters, locked, singleBranch, searchType } = this.props;
    if (!activeFilters.isEmpty()) {
      return (
        <div className="active-filters row">
          <div className="col-md-12">
            <ActiveFilters
              searchType={searchType}
              singleBranch={singleBranch}
              activeFilters={activeFilters}
              locked={locked} />
          </div>
        </div>
      );
    }

    return null;
  }

  renderDidNotFindExpandablePanel() {
    const { didNotFindOptions, chatWidgets, query } = this.props;

    return didNotFindOptions.get('isEnabled') && (
      <DidNotFindExpandablePanel
        query={query}
        didNotFindOptions={didNotFindOptions}
        chatWidgets={chatWidgets} />
    );
  }

  render() {
    const {
      activeFilters,
      currentView,
      groupedSearchEnabled,
      isFetching,
      pagination,
      rssURL,
      searchActions,
      sortBys
    } = this.props;

    return (
      <section className="cp-search-results">
        {this.renderActiveFilters()}
        <section className="result-controls btn-toolbar">
          <FacetDrawerToggle
            drawerActions={this.props.drawerActions}
            activeFilterCount={activeFilters
              .toList()
              .flatten(true)
              .count()} />
          <SortDropDown options={sortBys} paginationField="pagination_page" right />
          <ViewSelector
            currentView={currentView}
            groupedSearchEnabled={groupedSearchEnabled}
            searchActions={searchActions} />
          <PrintButton
            className="hidden-xs hidden-sm"
            url={this.getPrintUrl()}
            iconColor="#333" />
          <Pagination
            autoFocus
            isFetching={isFetching}
            textKey="Search.paginationInfo"
            page={pagination.get('page')}
            limit={pagination.get('limit')}
            count={pagination.get('count')}
            pages={pagination.get('pages')}
            queryField="pagination_page" />
        </section>

        <section className="results-list row">
          <div className="col-md-12 results-list-items">
            <ReactCSSTransitionGroup
              component="ul"
              className="results"
              transitionName="fade"
              transitionEnterTimeout={500}
              transitionLeaveTimeout={500}>
              {this.renderResultItems()}
              <FeaturedContentContainer />
            </ReactCSSTransitionGroup>
            {!this.context.mobileApp && <RSSButton url={rssURL} />}
          </div>
        </section>
        <AboveTheFoldOnlyServerRender skip>
          <section className="bottom-controls">
            <Pagination
              showPages
              isFetching={isFetching}
              queryField="pagination_page"
              textKey="Search.paginationInfo"
              page={pagination.get('page')}
              limit={pagination.get('limit')}
              count={pagination.get('count')}
              pages={pagination.get('pages')} />
            {this.renderGroupingToggle()}
          </section>
        </AboveTheFoldOnlyServerRender>
        <section className="did-not-find-panel">
          { this.renderDidNotFindExpandablePanel() }
        </section>
      </section>
    );
  }
}

const propTypes = {
  branches: ImmutablePropTypes.list,
  catalogSearch: PropTypes.object,
  catalogSearchOptions: PropTypes.object,
  currentUser: PropTypes.object,
  currentView: PropTypes.string.isRequired,
  groupedSearchEnabled: PropTypes.bool.isRequired,
  drawerActions: PropTypes.objectOf(PropTypes.func),
  featuredContent: PropTypes.object,
  featuredContentBlockPosition: PropTypes.number,
  isFetching: PropTypes.bool,
  libraryShortName: PropTypes.string.isRequired,
  listPresentationActions: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  locked: PropTypes.bool,
  rssURL: PropTypes.string,
  searchActions: PropTypes.objectOf(PropTypes.func),
  searchType: PropTypes.string,
  singleBranch: PropTypes.bool,
  sortBys: ImmutablePropTypes.list.isRequired,
  didNotFindOptions: ImmutablePropTypes.map,
  chatWidgets: ImmutablePropTypes.mapOf(PropTypes.object)
};

SearchResults.contextTypes = {
  mobileApp: PropTypes.bool
};

SearchResults.propTypes = propTypes;

export default SearchResults;



// WEBPACK FOOTER //
// ./app/components/search/SearchResults/SearchResults.jsx